/**
 * HHC.FR — Empêche le rechargement de page déclenché par EG Auto Add to Cart
 * quand Shopify plafonne une quantité faute de stock.
 *
 * LE PROBLÈME
 * -----------
 * Quand un client demande, depuis le drawer Cartly, plus d'unités qu'il n'y en
 * a en stock :
 *   1. Cartly appelle `/cart/change.js` ;
 *   2. Shopify plafonne à la quantité disponible et répond **HTTP 422** avec
 *      « X articles seulement ont été ajoutés à votre panier en raison de la
 *      disponibilité. » — message que Cartly affiche sous la ligne concernée ;
 *   3. EG constate que la quantité réelle ne correspond plus à celle qu'il
 *      attendait, appelle son backend `reapply-rules`, corrige ses cadeaux…
 *   4. …puis exécute `window.location.reload()`.
 *
 * Ce rechargement referme le drawer et efface le message : le client a
 * l'impression d'être renvoyé sur la page d'accueil au milieu de son achat.
 *
 * CE QUE FAIT CE FICHIER
 * ----------------------
 * Il bascule UN SEUL booléen de la configuration qu'EG lit au démarrage :
 *
 *     settings.disableReloadOnFailedAddition : false → true
 *
 * C'est le drapeau prévu par EG lui-même pour ce cas : dans son bundle, la
 * ligne est `L.settings.disableReloadOnFailedAddition || window.location.reload()`.
 * Le reste du chemin de correction d'EG est intact — `reapply-rules` tourne
 * toujours, et les cadeaux sont toujours ajoutés ou retirés par les appels
 * `cart/add.js` et `cart/update.js` marqués `_easygift_internal`. Seul le
 * rafraîchissement d'affichage disparaît : Cartly relit le panier en public
 * (`/cart.js`) et re-rend son drawer de lui-même.
 *
 * POURQUOI CÔTÉ THÈME, ET PAS DANS L'ADMIN EG
 * -------------------------------------------
 * Le réglage d'EG est scopé BOUTIQUE : il n'est pas isolable par thème, donc
 * impossible à valider sur une preview. Le comportement a été validé sur le
 * thème de preview #203600822615 avant d'être appliqué ici. La configuration
 * serveur d'EG reste volontairement à `false` : ce fichier est le seul point
 * de bascule, il est versionné avec le thème et se retire en une ligne.
 *
 * POURQUOI PAS UNE INTERCEPTION DE `location.reload()`
 * ----------------------------------------------------
 * Parce que c'est impossible, et que ce serait dangereux. Impossible : les
 * membres de l'interface `Location` sont `[LegacyUnforgeable]` dans la spec
 * HTML — propriétés propres, non-writable, non-configurable ; `location.reload
 * = f` échoue et `Object.defineProperty(location, 'reload', …)` lève une
 * TypeError. Dangereux : un override global de `reload()` toucherait la
 * navigation, le checkout, le sélecteur de pays et tout rechargement légitime.
 * Agir sur la configuration est chirurgical — cela ne concerne QUE le chemin
 * « failed addition » d'EG.
 *
 * OÙ EST LA CONFIGURATION D'EG, ET POURQUOI CE PLACEMENT
 * ------------------------------------------------------
 * Elle est embarquée dans la page, dans l'attribut `content` du nœud
 * `<meta id="easygift-shop">` rendu par l'app embed EG. Mesuré sur la page
 * réellement servie : Shopify injecte les app embed blocks à la TOUTE FIN du
 * `<head>`, après l'intégralité du contenu du thème — aucun emplacement du
 * `<head>` ne peut donc voir ce nœud. Ce fichier est chargé par une balise
 * CLASSIQUE (ni `async` ni `defer`) placée en TOUT DÉBUT de `<body>` : le
 * `<head>` y est entièrement parsé, le nœud existe, et le script d'EG — chargé
 * en `async` — n'a pas encore atteint la lecture du nœud, qui intervient très
 * tard dans sa séquence de démarrage.
 *
 * Trois filets, du plus déterministe au plus tardif :
 *   1. l'attribut `content` est réécrit ;
 *   2. `getAttribute` est épinglé SUR CE SEUL NŒUD, jamais sur `document` ni
 *      sur un prototype : aucune autre lecture du DOM n'est affectée ;
 *   3. si EG charge malgré tout ses réglages par le réseau, la réponse de
 *      `aca.506.io/public/store` est corrigée au vol — même booléen.
 *
 * CE QUE CE FICHIER NE FAIT PAS
 * ------------------------------
 * Aucune requête réseau créée (le filet 3 ne fait que transformer une réponse
 * déjà en transit). Aucun polling, aucun `setInterval`, aucun `setTimeout`,
 * aucun `MutationObserver`. Aucun override de `location`, de `document` ou
 * d'un prototype. Aucune interaction avec Cartly, la barre 59 €/89 €, le
 * panier, les produits, les prix, les stocks ou le checkout. Aucun autre
 * réglage EG n'est touché.
 *
 * SUPPRESSION
 * -----------
 * Retirer ce fichier et la balise `<script>` correspondante en début de
 * `<body>` dans `layout/theme.liquid`. Le comportement d'origine — avec
 * rechargement — revient immédiatement.
 */
(() => {
  'use strict';

  const FLAG = 'disableReloadOnFailedAddition';
  const SETTINGS_URL = 'aca.506.io/public/store';

  function log(message) {
    if (window.console && console.info) console.info('[HHC] ' + message);
  }

  /** Bascule le drapeau dans un objet de réglages EG. Retourne true si modifié. */
  function patchSettings(shop) {
    if (!shop || typeof shop !== 'object' || !shop.settings || typeof shop.settings !== 'object') return false;
    if (shop.settings[FLAG] === true) return false;
    shop.settings[FLAG] = true;
    return true;
  }

  /* ------------------------------------------------------------------ */
  /* 1. Configuration embarquée dans la page — chemin normal.            */
  /* ------------------------------------------------------------------ */

  try {
    const node = document.getElementById('easygift-shop');
    if (node) {
      const shop = JSON.parse(node.getAttribute('content'));
      if (patchSettings(shop)) {
        const patched = JSON.stringify(shop);
        node.setAttribute('content', patched);
        /* Épinglage sur CE SEUL nœud : toute lecture ultérieure renvoie la
           valeur corrigée, même si l'attribut était réécrit entre-temps. */
        const nativeGetAttribute = node.getAttribute.bind(node);
        node.getAttribute = function (name) {
          return name === 'content' ? patched : nativeGetAttribute(name);
        };
        log(FLAG + ' activé (configuration EG embarquée)');
      }
    }
  } catch (error) {
    /* Structure inattendue : on ne casse rien, le filet réseau reste actif. */
  }

  /* ------------------------------------------------------------------ */
  /* 2. Filet : configuration chargée par le réseau.                     */
  /* ------------------------------------------------------------------ */

  /* Une seule URL est concernée. Toutes les autres requêtes sont transmises
     telles quelles, sans être ni lues ni copiées. */
  if (typeof window.fetch === 'function') {
    const downstream = window.fetch.bind(window);
    window.fetch = function (...args) {
      let url = '';
      try {
        const input = args[0];
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (error) {
        url = '';
      }
      if (typeof url !== 'string' || url.indexOf(SETTINGS_URL) === -1) {
        return downstream(...args);
      }
      return downstream(...args).then((response) => {
        if (!response || !response.ok) return response;
        return response
          .clone()
          .json()
          .then((data) => {
            if (!patchSettings(data && data.shop)) return response;
            log(FLAG + ' activé (configuration EG réseau)');
            return new Response(JSON.stringify(data), {
              status: response.status,
              statusText: response.statusText,
              headers: response.headers,
            });
          })
          .catch(() => response);
      });
    };
  }
})();
